// Simple, branded PDF generator for the package draft.
// Avoids html2canvas (blurry screenshots) — composes a clean text + image layout with jsPDF.
import jsPDF from "jspdf";

const BRAND = { primary: "#1a2b5c", secondary: "#1a73e8", muted: "#6b7280", ink: "#0f1512" };

// jsPDF's default Helvetica is WinAnsi/Latin-1 only — Unicode characters (arrows, en/em
// dashes, bullets, ₹, stars, curly quotes) render as garbage glyphs. Convert them to
// ASCII equivalents before drawing any text. Applied via _ascii() across the file.
const UNI_MAP = {
  "→": "->", "←": "<-", "↔": "<->", "⇒": "=>", "⇐": "<=",
  "—": "-", "–": "-", "―": "-",
  "•": "*", "·": "-", "◦": "*",
  "₹": "Rs.",
  "★": "*", "☆": "*",
  "\u2018": "'", "\u2019": "'", "\u201C": '"', "\u201D": '"',
  "\u00A0": " ", "\u2009": " ", "\u200A": " ", "\u202F": " ",
  "…": "...",
};
function _ascii(text) {
  if (text === null || text === undefined) return "";
  let out = String(text);
  for (const [from, to] of Object.entries(UNI_MAP)) {
    out = out.split(from).join(to);
  }
  // Strip any remaining non-Latin-1 code points so jsPDF doesn't emit placeholders.
  // eslint-disable-next-line no-control-regex
  return out.replace(/[^\x00-\xFF]/g, "");
}

export async function downloadPackagePDF({ pkg, lead, destination, waPhone = "+91 95010 30117", returnBlob = false }) {
  if (!pkg) return null;

  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 48;
  let y = M;

  // --- Header band ---
  doc.setFillColor(BRAND.primary);
  doc.rect(0, 0, W, 64, "F");
  doc.setTextColor("#ffffff");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("VACATIONS PRO", M, 28);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(_ascii("Customised Holiday Makerz · Since 2005 · 4.8★ on Google"), M, 46);
  doc.setFontSize(10);
  doc.text(_ascii(waPhone), W - M, 36, { align: "right" });
  y = 96;

  // --- Hero image (best-effort; if CORS blocks, skip) ---
  if (pkg.hero_image) {
    try {
      const dataUrl = await _toDataURL(pkg.hero_image);
      if (dataUrl) {
        const imgH = 180;
        doc.addImage(dataUrl, "JPEG", M, y, W - 2 * M, imgH, undefined, "FAST");
        y += imgH + 20;
      }
    } catch { /* ignore */ }
  }

  // --- Title ---
  doc.setTextColor(BRAND.ink);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  _wrap(doc, pkg.title || "Your Custom Itinerary", W - 2 * M).forEach((line, i) => {
    doc.text(line, M, y + i * 26);
  });
  y += _wrap(doc, pkg.title || "", W - 2 * M).length * 26 + 8;

  // --- Summary ---
  if (pkg.summary) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(BRAND.muted);
    _wrap(doc, pkg.summary, W - 2 * M).forEach((line, i) => {
      doc.text(line, M, y + i * 15);
    });
    y += _wrap(doc, pkg.summary, W - 2 * M).length * 15 + 14;
  }

  // --- Traveller row ---
  doc.setDrawColor("#e5e7eb");
  doc.line(M, y, W - M, y);
  y += 16;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(BRAND.primary);
  const info = [
    ["TRAVELLER", lead?.name || "—"],
    ["PHONE", lead?.phone || "—"],
    ["DESTINATION", lead?.destination || destination || "—"],
    ["TRAVEL DATE", lead?.travel_date || "—"],
    ["TRAVELLERS", lead?.persons ? String(lead.persons) : "—"],
    ["HOTEL PREF.", lead?.hotel_preference || "—"],
    ["OCCASION", lead?.occasion || "—"],
    ["FLEXIBLE", lead?.dates_flexible === true ? "Yes" : lead?.dates_flexible === false ? "No" : "—"],
  ];
  // 2-row grid (4 cols × 2 rows) for the 8 fields
  const perRow = 4;
  const colW = (W - 2 * M) / perRow;
  info.forEach(([k, v], i) => {
    const row = Math.floor(i / perRow);
    const col = i % perRow;
    const x = M + col * colW;
    const yy = y + row * 28;
    doc.setTextColor(BRAND.muted);
    doc.setFontSize(8);
    doc.text(_ascii(k), x, yy);
    doc.setTextColor(BRAND.ink);
    doc.setFontSize(10);
    doc.text(_ascii(String(v)).slice(0, 22), x, yy + 12);
  });
  y += 58;

  // --- Flight routing --- (auto-grows to fit multi-line routes)
  if (pkg.flight_suggestion) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    const routeLines = _wrap(doc, pkg.flight_suggestion, W - 2 * M - 28);
    const boxH = 22 + routeLines.length * 14 + 8;
    doc.setDrawColor("#e5e7eb");
    doc.setFillColor("#f8f9fa");
    doc.roundedRect(M, y, W - 2 * M, boxH, 6, 6, "FD");
    doc.setTextColor(BRAND.primary);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text("FLIGHT ROUTING", M + 14, y + 16);
    doc.setTextColor(BRAND.ink);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    routeLines.forEach((line, i) => {
      doc.text(line, M + 14, y + 32 + i * 14);
    });
    y += boxH + 12;
  }

  // --- Days ---
  for (const d of (pkg.days || [])) {
    if (y > H - 180) { doc.addPage(); y = M; }

    // Day image (if available) — full width, shorter height
    if (d.image) {
      try {
        const dImg = await _toDataURL(d.image);
        if (dImg) {
          const imgH = 100;
          doc.addImage(dImg, "JPEG", M, y, W - 2 * M, imgH, undefined, "FAST");
          y += imgH + 10;
        }
      } catch { /* ignore */ }
    }

    doc.setDrawColor(BRAND.secondary);
    doc.setLineWidth(1.2);
    doc.line(M, y, M, y + 42);
    doc.setLineWidth(0.4);
    doc.setTextColor(BRAND.secondary);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text(_ascii(`DAY ${d.day} · ${(d.theme || "").toUpperCase()}`), M + 10, y + 12);
    const hl = (d.highlights || []).slice(0, 4);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(BRAND.ink);
    hl.forEach((h, i) => {
      const lines = _wrap(doc, `— ${h}`, W - 2 * M - 12);
      lines.forEach((line, li) => doc.text(line, M + 10, y + 30 + (i * 14) + li * 12));
    });
    y += 36 + hl.length * 14 + 14;
  }

  // --- Inclusions ---
  if (pkg.inclusions?.length) {
    if (y > H - 80) { doc.addPage(); y = M; }
    doc.setTextColor(BRAND.primary);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("INCLUSIONS", M, y);
    y += 14;
    doc.setTextColor(BRAND.ink);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    pkg.inclusions.forEach((inc) => {
      _wrap(doc, `· ${inc}`, W - 2 * M).forEach((line, i) => {
        doc.text(line, M, y + i * 13);
      });
      y += _wrap(doc, `· ${inc}`, W - 2 * M).length * 13;
    });
    y += 8;
  }

  // --- Budget ---
  if (pkg.estimated_budget_inr) {
    if (y > H - 60) { doc.addPage(); y = M; }
    doc.setDrawColor("#e5e7eb");
    doc.setFillColor("#f8f9fa");
    doc.roundedRect(M, y, W - 2 * M, 54, 6, 6, "FD");
    doc.setTextColor(BRAND.muted);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text("ESTIMATED BUDGET", M + 14, y + 18);
    doc.setTextColor(BRAND.primary);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text(_ascii(pkg.estimated_budget_inr), M + 14, y + 40);
    y += 66;
  }

  // --- Footer ---
  const footerY = H - 56;
  doc.setFillColor(BRAND.primary);
  doc.rect(0, footerY, W, 56, "F");
  doc.setTextColor("#ffffff");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("Ready to book? WhatsApp our sales team.", M, footerY + 22);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(_ascii(`${waPhone}  ·  alerts@vacationspro.com  ·  K-10 Tower, Feroze Gandhi Market, Ludhiana`), M, footerY + 40);

  const safe = (pkg.title || "vacations-pro-package").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  const filename = `${safe || "package"}.pdf`;
  if (returnBlob) {
    // Return { blob, filename } so callers can upload + download it themselves
    return { blob: doc.output("blob"), filename };
  }
  doc.save(filename);
  return null;
}

function _wrap(doc, text, maxW) {
  return doc.splitTextToSize(_ascii(text), maxW);
}

async function _toDataURL(url) {
  if (!url) return null;
  // Route external images through our backend proxy to avoid CORS / tainted-canvas failures in PDF.
  const backend = process.env.REACT_APP_BACKEND_URL;
  const proxied = /^https?:\/\//i.test(url) && backend
    ? `${backend}/api/image-proxy?url=${encodeURIComponent(url)}`
    : url;
  try {
    const res = await fetch(proxied);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = () => resolve(null);
      fr.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}
